/**
 * graphHelpers.ts - Shared graph traversal utilities
 *
 * Extracted from nodeReordering.ts to provide reusable graph operations
 * for reordering, layout, and other modules.
 *
 * Related:
 *   - utils/reordering.ts - Uses these for node/group reordering
 *   - utils/layoutEngine.ts - Uses these for layout calculations
 */

import type { Edge } from '@xyflow/react';

/**
 * Get the parent node ID for a given node (first incoming edge source).
 */
export function getParentNodeId(nodeId: string, edges: Edge[]): string | null {
	const incomingEdge = edges.find(e => e.target === nodeId);
	return incomingEdge?.source ?? null;
}

/**
 * Get all child node IDs for a given node (outgoing edge targets).
 */
export function getChildNodeIds(nodeId: string, edges: Edge[]): string[] {
	return edges.filter(e => e.source === nodeId).map(e => e.target);
}

/**
 * Get all descendants of a node (recursive children via BFS).
 */
export function getDescendantNodeIds(nodeId: string, edges: Edge[]): string[] {
	const descendants: string[] = [];
	const queue = [nodeId];

	while (queue.length > 0) {
		const currentId = queue.shift()!;
		const children = getChildNodeIds(currentId, edges);
		for (const childId of children) {
			if (!descendants.includes(childId)) {
				descendants.push(childId);
				queue.push(childId);
			}
		}
	}

	return descendants;
}

/**
 * Find the terminal node in a chain (node with no children, following first child).
 */
export function findChainTerminus(startNodeId: string, edges: Edge[]): string {
	let currentId = startNodeId;
	const visited = new Set<string>();
	const maxIterations = 1000;

	for (let i = 0; i < maxIterations; i++) {
		if (visited.has(currentId)) {
			return currentId;
		}
		visited.add(currentId);

		const children = getChildNodeIds(currentId, edges);
		if (children.length === 0) {
			return currentId;
		}
		currentId = children[0];
	}

	return currentId;
}

/**
 * Check if making nodeId a child of newParentId would create a cycle.
 */
export function wouldCreateCycle(nodeId: string, newParentId: string, edges: Edge[]): boolean {
	if (nodeId === newParentId) {
		return true;
	}
	const descendants = getDescendantNodeIds(nodeId, edges);
	return descendants.includes(newParentId);
}
